import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, DollarSign, Loader2, List, X, UtensilsCrossed } from 'lucide-react';
import { menusAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import SelectableItemsManager from '../components/menus/SelectableItemsManager';

const Menus = () => {
  const [menus, setMenus] = useState([]);
  const [filteredMenus, setFilteredMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [managingMenuId, setManagingMenuId] = useState(null);
  const [mainTab, setMainTab] = useState('menus');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    category: '',
    event_type: 'offsite',
    description: '',
    price_per_person: '',
    package_price: '',
    pricing_mode: 'per_person',
    taxable: true,
    is_all_inclusive: false,
    included_gratuity_rate: 0.15
  });

  const [itemFormData, setItemFormData] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    dietary: '',
    taxable: true
  });

  useEffect(() => {
    fetchMenus();
  }, []);

  useEffect(() => {
    filterMenus();
  }, [eventTypeFilter, menus]);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      const data = await menusAPI.getAll();
      setMenus(data);
    } catch (error) {
      toast({ title: 'Error loading menus', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const filterMenus = () => {
    if (eventTypeFilter === 'all') {
      setFilteredMenus(menus);
    } else {
      setFilteredMenus(menus.filter(m => m.event_type === eventTypeFilter || m.available_for?.includes(eventTypeFilter)));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const menuData = {
        ...formData,
        price_per_person: (formData.pricing_mode === 'flat') ? null : (formData.price_per_person ? parseFloat(formData.price_per_person) : null),
        package_price: (formData.pricing_mode === 'flat' && formData.package_price) ? parseFloat(formData.package_price) : null,
        taxable: formData.taxable,
        is_all_inclusive: formData.is_all_inclusive,
        included_gratuity_rate: formData.is_all_inclusive ? formData.included_gratuity_rate : 0.15
      };
      // pricing_mode is UI-only; don't send to API
      delete menuData.pricing_mode;
      
      if (editingMenu) {
        await menusAPI.update(editingMenu.id, menuData);
        toast({ title: 'Menu updated successfully!' });
      } else {
        await menusAPI.create({
          ...menuData,
          items: [],
          available_for: [formData.event_type]
        });
        toast({ title: 'Menu created successfully!' });
      }
      setIsDialogOpen(false);
      resetForm();
      fetchMenus();
    } catch (error) {
      toast({ title: 'Error saving menu', description: error.message, variant: 'destructive' });
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      const menu = menus.find(m => m.id === managingMenuId);
      if (!menu) return;

      let updatedItems = [...(menu.items || [])];
      
      const itemData = {
        id: editingItem?.id || `item-${Date.now()}`,
        name: itemFormData.name,
        description: itemFormData.description,
        category: itemFormData.category,
        price: itemFormData.price ? parseFloat(itemFormData.price) : null,
        dietary: itemFormData.dietary ? itemFormData.dietary.split(',').map(d => d.trim()) : [],
        taxable: itemFormData.taxable
      };

      if (editingItem) {
        updatedItems = updatedItems.map(item => item.id === editingItem.id ? itemData : item);
        toast({ title: 'Item updated!' });
      } else {
        updatedItems.push(itemData);
        toast({ title: 'Item added!' });
      }

      await menusAPI.update(managingMenuId, { items: updatedItems });
      setIsItemDialogOpen(false);
      resetItemForm();
      fetchMenus();
    } catch (error) {
      toast({ title: 'Error saving item', description: error.message, variant: 'destructive' });
    }
  };

  const handleDeleteItem = async (menuId, itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const menu = menus.find(m => m.id === menuId);
      const updatedItems = menu.items.filter(item => item.id !== itemId);
      await menusAPI.update(menuId, { items: updatedItems });
      toast({ title: 'Item deleted!' });
      fetchMenus();
    } catch (error) {
      toast({ title: 'Error deleting item', description: error.message, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      category: '', 
      event_type: 'offsite', 
      description: '', 
      price_per_person: '',
      package_price: '',
      pricing_mode: 'per_person',
      taxable: true,
      is_all_inclusive: false,
      included_gratuity_rate: 0.15
    });
    setEditingMenu(null);
  };

  const resetItemForm = () => {
    setItemFormData({ name: '', description: '', category: '', price: '', dietary: '', taxable: true });
    setEditingItem(null);
  };

  const handleEdit = (menu) => {
    setEditingMenu(menu);
    setFormData({
      name: menu.name,
      category: menu.category,
      event_type: menu.event_type,
      description: menu.description,
      price_per_person: menu.price_per_person?.toString() || '',
      package_price: menu.package_price?.toString() || '',
      pricing_mode: menu.package_price ? 'flat' : 'per_person',
      taxable: menu.taxable !== false,
      is_all_inclusive: menu.is_all_inclusive || false,
      included_gratuity_rate: menu.included_gratuity_rate || 0.15
    });
    setIsDialogOpen(true);
  };

  const handleEditItem = (menuId, item) => {
    setManagingMenuId(menuId);
    setEditingItem(item);
    setItemFormData({
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      price: item.price?.toString() || '',
      dietary: item.dietary?.join(', ') || '',
      taxable: item.taxable !== false
    });
    setIsItemDialogOpen(true);
  };

  const handleAddItem = (menuId) => {
    setManagingMenuId(menuId);
    resetItemForm();
    setIsItemDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this menu?')) return;
    try {
      await menusAPI.delete(id);
      toast({ title: 'Menu deleted successfully!' });
      fetchMenus();
    } catch (error) {
      toast({ title: 'Error deleting menu', description: error.message, variant: 'destructive' });
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Menu Management</h1>
          <p className="text-gray-600">Create and manage your catering menus and selectable items</p>
        </div>
      </div>

      {/* Main Tab Navigation */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="mb-6">
        <TabsList className="bg-white p-1">
          <TabsTrigger 
            value="menus" 
            className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white px-6"
          >
            <List size={16} className="mr-2" />
            Menus
          </TabsTrigger>
          <TabsTrigger 
            value="selectable" 
            className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white px-6"
          >
            <UtensilsCrossed size={16} className="mr-2" />
            Selectable Items
          </TabsTrigger>
        </TabsList>

        {/* Menus Tab Content */}
        <TabsContent value="menus" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white">
                  <Plus size={20} className="mr-2" />
                  Add Menu
                </Button>
              </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingMenu ? 'Edit Menu' : 'Create New Menu'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Menu Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Buffet">Buffet</SelectItem>
                    <SelectItem value="Premium Buffet">Premium Buffet</SelectItem>
                    <SelectItem value="Plated">Plated</SelectItem>
                    <SelectItem value="Interactive">Interactive</SelectItem>
                    <SelectItem value="Hors d'Oeuvres">Hors d'Oeuvres</SelectItem>
                    <SelectItem value="Late Night">Late Night</SelectItem>
                    <SelectItem value="Tournament">Tournament</SelectItem>
                    <SelectItem value="bar_package">Bar Package</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="event_type">Event Type</Label>
                <Select value={formData.event_type} onValueChange={(value) => setFormData({ ...formData, event_type: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="onsite">Onsite</SelectItem>
                    <SelectItem value="offsite">Offsite</SelectItem>
                    <SelectItem value="tournament">Tournament</SelectItem>
                    <SelectItem value="cocktail_coach">Cocktail Coach</SelectItem>
                    <SelectItem value="pickup">Catering Pick Up</SelectItem>
                    <SelectItem value="dropoff">Catering Drop Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  required
                />
              </div>
              <div>
                <Label>Pricing Mode</Label>
                <div className="flex gap-2 mb-2">
                  {[{k:'per_person',label:'Per Person'},{k:'flat',label:'Flat Rate'}].map(opt => {
                    const active = (formData.pricing_mode || 'per_person') === opt.k;
                    return (
                      <button key={opt.k} type="button" onClick={() => setFormData({...formData, pricing_mode: opt.k})}
                        data-testid={`menu-pricing-mode-${opt.k}`}
                        className={`px-3 py-1.5 text-xs rounded-md border transition ${active ? 'bg-[#1e3a5f] text-white border-[#1e3a5f]' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {(formData.pricing_mode || 'per_person') === 'flat' ? (
                  <div>
                    <Label htmlFor="package_price" className="text-xs">Flat Package Price ($)</Label>
                    <Input id="package_price" type="number" step="0.01"
                      value={formData.package_price || ''}
                      onChange={(e) => setFormData({ ...formData, package_price: e.target.value })} />
                    <p className="text-[10px] text-gray-500 mt-1">Fixed price regardless of guest count — ideal for pickup/dropoff platters, set menus, or packages.</p>
                  </div>
                ) : (
                  <div>
                    <Label htmlFor="price" className="text-xs">Price Per Person ($)</Label>
                    <Input id="price" type="number" step="0.01"
                      value={formData.price_per_person}
                      onChange={(e) => setFormData({ ...formData, price_per_person: e.target.value })} />
                  </div>
                )}
              </div>
              
              {/* Tax & Pricing Options */}
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <h4 className="font-medium text-sm text-gray-700">Pricing Options</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="taxable"
                    checked={formData.taxable}
                    onChange={(e) => setFormData({ ...formData, taxable: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="taxable" className="text-sm cursor-pointer">Taxable (13% HST)</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="is_all_inclusive"
                    checked={formData.is_all_inclusive}
                    onChange={(e) => setFormData({ ...formData, is_all_inclusive: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_all_inclusive" className="text-sm cursor-pointer">
                    All-Inclusive Price (includes tax & gratuity)
                  </Label>
                </div>
                {formData.is_all_inclusive && (
                  <div className="ml-6">
                    <Label className="text-sm">Included Gratuity Rate</Label>
                    <Select 
                      value={formData.included_gratuity_rate.toString()} 
                      onValueChange={(value) => setFormData({ ...formData, included_gratuity_rate: parseFloat(value) })}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0.15">15%</SelectItem>
                        <SelectItem value="0.18">18%</SelectItem>
                        <SelectItem value="0.20">20%</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">
                      Tax & gratuity will be broken out from this all-in price
                    </p>
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold">
                {editingMenu ? 'Update Menu' : 'Create Menu'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Item Management Dialog */}
        <Dialog open={isItemDialogOpen} onOpenChange={(open) => {
          setIsItemDialogOpen(open);
          if (!open) resetItemForm();
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Menu Item' : 'Add Menu Item'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleItemSubmit} className="space-y-4">
              <div>
                <Label>Item Name *</Label>
                <Input value={itemFormData.name} onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })} required />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={itemFormData.description} onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })} rows={2} />
              </div>
              <div>
                <Label>Category</Label>
                <Input value={itemFormData.category} onChange={(e) => setItemFormData({ ...itemFormData, category: e.target.value })} placeholder="Appetizer, Main, Side, Dessert" />
              </div>
              <div>
                <Label>Price ($)</Label>
                <Input type="number" step="0.01" value={itemFormData.price} onChange={(e) => setItemFormData({ ...itemFormData, price: e.target.value })} />
              </div>
              <div>
                <Label>Dietary (comma-separated)</Label>
                <Input value={itemFormData.dietary} onChange={(e) => setItemFormData({ ...itemFormData, dietary: e.target.value })} placeholder="Vegetarian, GF, Vegan" />
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="taxable" checked={itemFormData.taxable} onChange={(e) => setItemFormData({ ...itemFormData, taxable: e.target.checked })} className="w-4 h-4" />
                <Label htmlFor="taxable">Taxable</Label>
              </div>
              <Button type="submit" className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold">
                {editingItem ? 'Update Item' : 'Add Item'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={eventTypeFilter} onValueChange={setEventTypeFilter} className="mb-6">
        <TabsList className="bg-white">
          <TabsTrigger value="all" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">All Menus</TabsTrigger>
          <TabsTrigger value="onsite" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">Onsite</TabsTrigger>
          <TabsTrigger value="offsite" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">Offsite</TabsTrigger>
          <TabsTrigger value="tournament" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">Tournaments</TabsTrigger>
          <TabsTrigger value="cocktail_coach" className="data-[state=active]:bg-[#1e3a5f] data-[state=active]:text-white">Cocktail Coach</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="animate-spin text-[#1e3a5f]" size={48} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {filteredMenus.map((menu) => (
            <Card key={menu.id} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
              <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg mb-2">{menu.name}</CardTitle>
                    <div className="flex gap-2">
                      <span className="inline-block px-3 py-1 bg-[#c9a227] text-[#1e3a5f] text-xs font-semibold rounded-full">
                        {menu.category}
                      </span>
                      <span className="inline-block px-3 py-1 bg-white text-[#1e3a5f] text-xs font-semibold rounded-full">
                        {menu.event_type}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleAddItem(menu.id)} size="sm" variant="outline" className="bg-white text-[#1e3a5f]">
                      <Plus size={16} className="mr-1" />Items
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <p className="text-gray-600 text-sm mb-4">{menu.description}</p>
                
                {menu.price_per_person && (
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 text-[#1e3a5f] font-bold text-xl">
                      <DollarSign size={20} />
                      <span>{menu.price_per_person}</span>
                      <span className="text-sm font-normal text-gray-600">per person</span>
                    </div>
                  </div>
                )}

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-xs font-semibold text-gray-700">MENU ITEMS ({menu.items?.length || 0})</p>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-3">
                    {menu.items && menu.items.length > 0 ? menu.items.map((item) => (
                      <div key={item.id} className="flex justify-between items-start p-2 bg-gray-50 rounded hover:bg-gray-100">
                        <div className="flex-1">
                          <p className="font-semibold text-sm text-[#1e3a5f]">{item.name}</p>
                          <p className="text-xs text-gray-600">{item.category} {item.dietary?.length > 0 && `• ${item.dietary.join(', ')}`}</p>
                          {item.price && <p className="text-xs text-gray-500">${item.price}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button onClick={() => handleEditItem(menu.id, item)} size="sm" variant="ghost" className="h-6 px-2">
                            <Edit size={12} />
                          </Button>
                          <Button onClick={() => handleDeleteItem(menu.id, item.id)} size="sm" variant="ghost" className="h-6 px-2 text-red-500">
                            <X size={12} />
                          </Button>
                        </div>
                      </div>
                    )) : (
                      <p className="text-gray-400 text-center py-4">No items added yet</p>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={() => handleEdit(menu)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
                  >
                    <Edit size={16} className="mr-1" />
                    Edit Menu
                  </Button>
                  <Button
                    onClick={() => handleDelete(menu.id)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && filteredMenus.length === 0 && (
        <div className="text-center py-20">
          <p className="text-gray-500 text-lg">No menus found for this category</p>
        </div>
      )}
        </TabsContent>

        {/* Selectable Items Tab Content */}
        <TabsContent value="selectable" className="mt-6">
          <SelectableItemsManager />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Menus;
